import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import OfferBanner from './OfferBanner';
import { TypeAnimation } from 'react-type-animation';
import config from '../config';

const Header = ({ openModal }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // ---- CLARITY helper ----
  const track = (name, props = {}) => {
    if (typeof window !== 'undefined' && window.clarity) {
      window.clarity('event', name, props);
    }
  };

  const isActive = (path) => location.pathname === path;

  const handleFaqClick = () => {
    // CLARITY: FAQ click
    track('faq_click', { source: 'header' });

    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const faqSection = document.getElementById('faq');
        if (faqSection) faqSection.scrollIntoView({ behavior: 'smooth' });
      }, 200);
    } else {
      const faqSection = document.getElementById('faq');
      if (faqSection) faqSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const activeClass = 'text-orange-600 font-semibold border-b-2 border-orange-600';

  return (
    <header className="sticky top-0 z-50">
      {/* <OfferBanner /> */}

      <div className="bg-white/80 backdrop-blur-md shadow-md text-gray-800 px-6 py-4">
        <div className="flex justify-between items-center">
          {/* Logo & Tagline */}
          <div className="flex items-center gap-4">
            <img src="/ssrlogo3.png" alt="SSRPRO Logo" className="w-12 h-auto" />
            <div className="flex flex-col">
              <div className="text-2xl font-bold leading-tight">
                <Link
                  to="/"
                  className="hover:text-orange-500 transition-colors"
                  onClick={() => track('nav_click', { item: 'home_logo' })}
                >
                  myBOQ
                </Link>
              </div>

              {/* Powered by directly under the myBOQ name */}
              <a
                href="https://siliconmount.com"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-0.5 inline-flex items-center gap-1 text-[11px] md:text-xs text-gray-500 hover:text-orange-600 hover:underline transition"
                aria-label="Powered by SiliconMount Tech Services (opens in new tab)"
                onClick={() => track('powered_by_click', { placement: 'logo_block' })}
              >
                <span>Powered by</span>
                <span className="font-semibold">SiliconMount Tech Services</span>
              </a>

              <TypeAnimation
                sequence={[
                  'Estimate.', 1500,
                  'Automate.', 1500,
                  'Comply.', 1500,
                  'Estimate. Automate. Comply.', 2500
                ]}
                speed={50}
                wrapper="span"
                repeat={Infinity}
                className="text-xs italic text-gray-600 tracking-wide"
              />
            </div>
          </div>

          {/* Hamburger Toggle */}
          <div className="md:hidden">
            <button
              onClick={() => {
                const next = !menuOpen;
                setMenuOpen(next);
                track('mobile_menu_toggle', { open: next });
              }}
              className="focus:outline-none"
              aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {menuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6">
            <Link
              to="/"
              className={`font-bold text-lg hover:text-orange-500 transition-colors duration-200 ${isActive('/') ? activeClass : ''}`}
              onClick={() => track('nav_click', { item: 'home' })}
            >
              Home
            </Link>

            <Link
              to="/features"
              className={`font-semibold hover:text-orange-500 transition-colors duration-200 ${isActive('/features') ? activeClass : ''}`}
              onClick={() => track('nav_click', { item: 'features' })}
            >
              Features
            </Link>

            {/* 🆕 Blog link */}
            <Link
              to="/blog"
              className={`font-semibold hover:text-orange-500 transition-colors duration-200 ${isActive('/blog') ? activeClass : ''}`}
              onClick={() => track('nav_click', { item: 'blog' })}
            >
              Blog
            </Link>

            <button
              onClick={handleFaqClick}
              className="font-semibold hover:text-orange-500 transition-colors duration-200"
            >
              FAQ
            </button>

            <button
              onClick={() => {
                track('apply_cta_click', { placement: 'header', target: 'login' });
                window.open(config.signInUrl, '_blank');
              }}
              className="bg-orange-100 text-gray-800 font-bold px-4 py-2 rounded-full hover:bg-orange-200 transition"
            >
              Login
            </button>

            <Link
              to="/signup"
              className="bg-orange-100 text-gray-800 font-bold px-4 py-2 rounded-full hover:bg-orange-200 transition"
              onClick={() => track('apply_cta_click', { placement: 'header', target: 'signup' })}
            >
              Signup
            </Link>

            <button
              onClick={() => {
                track('contact_click', { placement: 'header' });
                openModal();
              }}
              className="font-semibold hover:text-orange-500 transition-colors"
            >
              Contact
            </button>
          </nav>
        </div>

        {/* Mobile Navigation */}
        {menuOpen && (
          <div className="md:hidden mt-4 flex flex-col gap-4 items-center text-gray-800">
            <Link
              to="/"
              onClick={() => { setMenuOpen(false); track('nav_click', { item: 'home', source: 'mobile' }); }}
              className={`hover:text-orange-500 transition-colors ${isActive('/') ? activeClass : ''}`}
            >
              Home
            </Link>

            <Link
              to="/features"
              onClick={() => { setMenuOpen(false); track('nav_click', { item: 'features', source: 'mobile' }); }}
              className={`hover:text-orange-500 transition-colors ${isActive('/features') ? activeClass : ''}`}
            >
              Features
            </Link>

            {/* 🆕 Blog link for mobile */}
            <Link
              to="/blog"
              onClick={() => { setMenuOpen(false); track('nav_click', { item: 'blog', source: 'mobile' }); }}
              className={`hover:text-orange-500 transition-colors ${isActive('/blog') ? activeClass : ''}`}
            >
              Blog
            </Link>

            <button
              onClick={() => { handleFaqClick(); setMenuOpen(false); }}
              className="hover:text-orange-500 transition-colors"
            >
              FAQ
            </button>

            <Link
              to="/signup"
              onClick={() => { setMenuOpen(false); track('apply_cta_click', { placement: 'mobile_header', target: 'signup' }); }}
              className="w-3/4 text-center bg-orange-100 text-gray-800 font-bold px-4 py-2 rounded-lg hover:bg-orange-200 transition"
            >
              Signup
            </Link>

            <button
              onClick={() => {
                track('apply_cta_click', { placement: 'mobile_header', target: 'login' });
                window.open(config.signInUrl, '_blank');
                setMenuOpen(false);
              }}
              className="w-3/4 text-center bg-orange-100 text-gray-800 font-bold px-4 py-2 rounded-lg hover:bg-orange-200 transition"
            >
              Login
            </button>

            <button
              onClick={() => {
                track('contact_click', { placement: 'mobile_header' });
                openModal();
                setMenuOpen(false);
              }}
              className="hover:text-orange-500 transition-colors"
            >
              Contact
            </button>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
